import React, { useState, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { CATEGORIES } from './bookFormConstants.js';

interface CategoryDropdownProps {
  category: string;
  onSelectCategory: (cat: string) => void;
  isCustomCategory: boolean;
  setIsCustomCategory: (val: boolean) => void;
  customCategory: string;
  setCustomCategory: (val: string) => void;
  error?: string;
}

export const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  category,
  onSelectCategory,
  isCustomCategory,
  setIsCustomCategory,
  customCategory,
  setCustomCategory,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.category-dropdown-container')) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1">
        Kategori / Genre <span className="text-rose-500">*</span>
      </label>

      {!isCustomCategory ? (
        <div className="relative category-dropdown-container">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between px-3.5 py-2 text-sm border border-slate-300 rounded-xl bg-white hover:bg-slate-50 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 cursor-pointer text-left transition duration-150"
          >
            <span className="text-slate-800">{category || 'Pilih Kategori'}</span>
            <ChevronDown
              className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {isOpen && (
            <div className="absolute z-50 w-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto overflow-x-hidden animate-in fade-in slide-in-from-top-1 duration-100">
              <div className="p-1.5 space-y-0.5">
                {CATEGORIES.map((c, cIdx) => {
                  const isSelected = category === c;
                  return (
                    <button
                      key={`${c}-${cIdx}`}
                      type="button"
                      onClick={() => {
                        onSelectCategory(c);
                        setIsOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium rounded-lg text-left transition duration-150 cursor-pointer ${
                        isSelected
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span>{c}</span>
                      {isSelected && <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
                    </button>
                  );
                })}

                <div className="h-px bg-slate-100 my-1" />

                <button
                  type="button"
                  onClick={() => {
                    setIsCustomCategory(true);
                    setCustomCategory('');
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-indigo-600 hover:bg-indigo-50/50 rounded-lg text-left transition duration-150 cursor-pointer"
                >
                  <span>✍️ Tulis Kategori Kustom...</span>
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-1.5 animate-in slide-in-from-top-1 duration-150">
          <div className="flex gap-2">
            <input
              type="text"
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              placeholder="Contoh: Biografi, Filsafat Barat, Sejarah Populer"
              className={`flex-1 px-3.5 py-2 text-sm border rounded-xl bg-white transition duration-150 ${
                error
                  ? 'border-rose-300 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 bg-rose-50/20'
                  : 'border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600'
              }`}
            />
            <button
              type="button"
              onClick={() => {
                setIsCustomCategory(false);
                onSelectCategory(CATEGORIES[0]);
              }}
              className="px-3 py-2 text-xs font-semibold border border-slate-300 hover:bg-slate-50 text-slate-600 rounded-xl transition cursor-pointer"
            >
              Batal
            </button>
          </div>
        </div>
      )}
      {error && (
        <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
          <span>{error}</span>
        </p>
      )}
    </div>
  );
};
